import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import '../App.css';

export default function GamePage() {
    const { gameId } = useParams();
    const { currentUser, userProfile } = useAuth();
    const navigate = useNavigate();

    const [game, setGame] = useState(null);
    const [players, setPlayers] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [host, setHost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [emailNotification, setEmailNotification] = useState(false);
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    const MAX_PLAYERS = 10;

    useEffect(() => {
        loadGame();
    }, [gameId]);

    useEffect(() => {
        if (userProfile?.isAdmin) {
            loadAllUsers();
        }
    }, [userProfile]);

    useEffect(() => {
        if (!game?.registrationOpenTime) return;

        const updateCountdown = () => {
            const now = new Date();
            const registrationOpenTime = new Date(game.registrationOpenTime.seconds * 1000);
            const difference = registrationOpenTime - now;

            if (difference <= 0) {
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            setCountdown({ days, hours, minutes, seconds });
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [game?.registrationOpenTime]);

    async function loadGame() {
        try {
            const gameDoc = await getDoc(doc(db, 'games', gameId));

            if (!gameDoc.exists()) {
                setError('המשחק לא נמצא');
                setLoading(false);
                return;
            }

            const gameData = gameDoc.data();
            setGame(gameData);

            // Load host info
            if (gameData.hostUserId) {
                const hostDoc = await getDoc(doc(db, 'users', gameData.hostUserId));
                if (hostDoc.exists()) {
                    setHost({ id: gameData.hostUserId, ...hostDoc.data() });
                }
            }

            // Filter out invalid user IDs
            const validPlayers = (gameData.players || []).filter(id => id && id.trim());
            const validWaitlist = (gameData.waitlist || []).filter(id => id && id.trim());

            // Load player details
            if (validPlayers.length > 0) {
                const playerDetails = await Promise.all(
                    validPlayers.map(async (userId) => {
                        try {
                            const userDoc = await getDoc(doc(db, 'users', userId));
                            if (userDoc.exists()) {
                                return { id: userId, ...userDoc.data() };
                            } else {
                                return { id: userId, displayName: 'משתמש לא ידוע', email: '' };
                            }
                        } catch (err) {
                            console.error('Error loading user:', userId, err);
                            return { id: userId, displayName: 'שגיאה בטעינת משתמש', email: '' };
                        }
                    })
                );
                setPlayers(playerDetails);
            } else {
                setPlayers([]);
            }

            // Load waitlist details
            if (validWaitlist.length > 0) {
                const waitlistDetails = await Promise.all(
                    validWaitlist.map(async (userId) => {
                        try {
                            const userDoc = await getDoc(doc(db, 'users', userId));
                            if (userDoc.exists()) {
                                return { id: userId, ...userDoc.data() };
                            } else {
                                return { id: userId, displayName: 'משתמש לא ידוע', email: '' };
                            }
                        } catch (err) {
                            console.error('Error loading user:', userId, err);
                            return { id: userId, displayName: 'שגיאה בטעינת משתמש', email: '' };
                        }
                    })
                );
                setWaitlist(waitlistDetails);

                // Check if current user has email notification enabled
                if (currentUser && gameData.waitlistNotifications?.[currentUser.uid]) {
                    setEmailNotification(true);
                }
            } else {
                setWaitlist([]);
            }

            setLoading(false);
        } catch (err) {
            console.error('Error loading game:', err);
            setError('שגיאה בטעינת המשחק: ' + err.message);
            setLoading(false);
        }
    }

    async function loadAllUsers() {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const usersData = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAllUsers(usersData);
        } catch (err) {
            console.error('Error loading users:', err);
        }
    }

    async function handleJoin() {
        try {
            setError('');

            const gameRef = doc(db, 'games', gameId);
            const gameDoc = await getDoc(gameRef);
            const gameData = gameDoc.data();

            const currentPlayers = (gameData.players || []).filter(id => id && id.trim());
            const currentWaitlist = (gameData.waitlist || []).filter(id => id && id.trim());

            if (currentPlayers.includes(currentUser.uid)) {
                setError('אתה כבר רשום כשחקן');
                return;
            }

            if (currentWaitlist.includes(currentUser.uid)) {
                setError('אתה כבר ברשימת ההמתנה');
                return;
            }

            if (currentPlayers.length < MAX_PLAYERS) {
                await updateDoc(gameRef, {
                    players: arrayUnion(currentUser.uid)
                });
            } else {
                await updateDoc(gameRef, {
                    waitlist: arrayUnion(currentUser.uid)
                });
            }

            await loadGame();

        } catch (err) {
            console.error('Error joining game:', err);
            setError('שגיאה בהצטרפות למשחק: ' + err.message);
        }
    }

    async function handleLeave() {
        // Show confirmation dialog before leaving
        const confirmMessage = 'האם אתה בטוח שברצונך לצאת מהמשחק?';
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setError('');

            const gameRef = doc(db, 'games', gameId);
            const gameDoc = await getDoc(gameRef);
            const gameData = gameDoc.data();

            const currentPlayers = (gameData.players || []).filter(id => id && id.trim());
            const currentWaitlist = (gameData.waitlist || []).filter(id => id && id.trim());

            if (currentPlayers.includes(currentUser.uid)) {
                await updateDoc(gameRef, {
                    players: arrayRemove(currentUser.uid)
                });

                if (currentWaitlist.length > 0) {
                    const firstWaitlist = currentWaitlist[0];
                    await updateDoc(gameRef, {
                        waitlist: arrayRemove(firstWaitlist),
                        players: arrayUnion(firstWaitlist)
                    });
                }
            } else if (currentWaitlist.includes(currentUser.uid)) {
                // Remove from waitlist and clear notification preference
                const updates = {
                    waitlist: arrayRemove(currentUser.uid)
                };

                // Remove notification preference if exists
                if (gameData.waitlistNotifications?.[currentUser.uid]) {
                    const waitlistNotifications = { ...gameData.waitlistNotifications };
                    delete waitlistNotifications[currentUser.uid];
                    updates.waitlistNotifications = waitlistNotifications;
                }

                await updateDoc(gameRef, updates);
                setEmailNotification(false);
            }

            await loadGame();

        } catch (err) {
            console.error('Error leaving game:', err);
            setError('שגיאה ביציאה מהמשחק: ' + err.message);
        }
    }

    async function handleEmailNotificationToggle() {
        if (!currentUser || !isInWaitlist) return;

        try {
            const gameRef = doc(db, 'games', gameId);
            const gameDoc = await getDoc(gameRef);
            const gameData = gameDoc.data();

            const waitlistNotifications = gameData.waitlistNotifications || {};
            const newValue = !emailNotification;

            if (newValue) {
                // Enable notification
                waitlistNotifications[currentUser.uid] = true;
            } else {
                // Disable notification
                delete waitlistNotifications[currentUser.uid];
            }

            await updateDoc(gameRef, {
                waitlistNotifications: waitlistNotifications
            });

            setEmailNotification(newValue);
        } catch (err) {
            console.error('Error toggling email notification:', err);
            setError('שגיאה בעדכון הגדרות התראה: ' + err.message);
        }
    }

    async function handleAdminAddToWaitlist(userId) {
        try {
            setError('');

            const gameRef = doc(db, 'games', gameId);
            const gameDoc = await getDoc(gameRef);
            const gameData = gameDoc.data();

            const currentPlayers = (gameData.players || []).filter(id => id && id.trim());
            const currentWaitlist = (gameData.waitlist || []).filter(id => id && id.trim());

            if (currentPlayers.includes(userId)) {
                setError('משתמש זה כבר רשום כשחקן');
                return;
            }

            if (currentWaitlist.includes(userId)) {
                setError('משתמש זה כבר ברשימת ההמתנה');
                return;
            }

            await updateDoc(gameRef, {
                waitlist: arrayUnion(userId)
            });

            await loadGame();
            setError('');
            alert('המשתמש נוסף לרשימת ההמתנה בהצלחה!');

        } catch (err) {
            console.error('Error adding user to waitlist:', err);
            setError('שגיאה בהוספת משתמש לרשימת ההמתנה: ' + err.message);
        }
    }

    function handleAddToCalendar() {
        if (!game || !game.date) return;

        const gameDate = new Date(game.date.seconds * 1000);
        const endDate = new Date(gameDate.getTime() + 4 * 60 * 60 * 1000); // 4 hours later

        // Format dates for calendar (YYYYMMDDTHHMMSSZ)
        const formatDate = (date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const title = encodeURIComponent(game.name);
        const details = encodeURIComponent(
            `משחק פוקר${host ? ' באירוח ' + host.displayName : ''}\n\n` +
            (game.notes ? `הערות: ${game.notes}\n\n` : '') +
            `קישור: ${window.location.href}`
        );
        const location = encodeURIComponent(game.address || '');

        const startDate = formatDate(gameDate);
        const endDateFormatted = formatDate(endDate);

        // Google Calendar URL
        const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDateFormatted}&details=${details}&location=${location}`;

        window.open(googleCalendarUrl, '_blank');
    }

    const isRegistered = currentUser && (
        players.some(p => p.id === currentUser.uid) ||
        waitlist.some(w => w.id === currentUser.uid)
    );

    const isInPlayers = currentUser && players.some(p => p.id === currentUser.uid);
    const isInWaitlist = currentUser && waitlist.some(w => w.id === currentUser.uid);

    // Check if user is a hosting player with valid tag
    const isHostingPlayer = userProfile?.hostingPlayerTag &&
                           userProfile?.hostingPlayerTagExpiry &&
                           new Date(userProfile.hostingPlayerTagExpiry.seconds * 1000) > new Date();

    // Check if registration is open (using Israel timezone)
    const now = new Date();
    const registrationOpenTime = game?.registrationOpenTime
        ? new Date(game.registrationOpenTime.seconds * 1000)
        : null;
    const isRegistrationOpen = !registrationOpenTime || now >= registrationOpenTime || isHostingPlayer;

    if (loading) {
        return <div style={styles.loading}>טוען...</div>;
    }

    if (error && !game) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>{error}</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.topNav}>
                    <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
                        ← חזרה לדשבורד
                    </button>
                </div>

                <h1 style={styles.title}>
                    🎰 {game.name}
                    {host && <span style={styles.hostBadge}>🏠 באירוח {host.displayName}</span>}
                </h1>

                {game.date && (
                    <div style={styles.dateSection}>
                        <p style={styles.date}>
                            📅 {new Date(game.date.seconds * 1000).toLocaleDateString('he-IL', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                        </p>
                        {isRegistered && (
                            <button onClick={handleAddToCalendar} style={styles.calendarButton}>
                                📅 הוסף ליומן
                            </button>
                        )}
                    </div>
                )}

                {game.address && (
                    <div style={styles.infoBox}>
                        <strong>📍 כתובת:</strong>
                        <p style={styles.infoText}>{game.address}</p>
                    </div>
                )}

                {game.notes && (
                    <div style={styles.infoBox}>
                        <strong>📝 הערות:</strong>
                        <p style={styles.infoText}>{game.notes}</p>
                    </div>
                )}

                {!isRegistrationOpen && registrationOpenTime && !isHostingPlayer && (
                    <div style={styles.registrationClosedBanner}>
                        <div style={styles.countdownTitle}>
                            🔒 ההרשמה תיפתח בקרוב
                        </div>
                        <div style={styles.countdownTimer}>
                            <div style={styles.countdownSegment}>
                                <div style={styles.countdownNumber}>{countdown.seconds}</div>
                                <div style={styles.countdownLabel}>שניות</div>
                            </div>
                            <div style={styles.countdownSeparator}>:</div>
                            <div style={styles.countdownSegment}>
                                <div style={styles.countdownNumber}>{countdown.minutes}</div>
                                <div style={styles.countdownLabel}>דקות</div>
                            </div>
                            <div style={styles.countdownSeparator}>:</div>
                            <div style={styles.countdownSegment}>
                                <div style={styles.countdownNumber}>{countdown.hours}</div>
                                <div style={styles.countdownLabel}>שעות</div>
                            </div>
                            <div style={styles.countdownSeparator}>:</div>
                            <div style={styles.countdownSegment}>
                                <div style={styles.countdownNumber}>{countdown.days}</div>
                                <div style={styles.countdownLabel}>ימים</div>
                            </div>
                        </div>
                        <div style={styles.countdownDate}>
                            פתיחה: {registrationOpenTime.toLocaleDateString('he-IL', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </div>
                    </div>
                )}

                {/* Hosting Player Privilege Banner */}
                {isHostingPlayer && registrationOpenTime && now < registrationOpenTime && !isRegistered && (
                    <div style={styles.hostingPlayerBanner}>
                        <div style={styles.hostingPlayerTitle}>
                            🏠 זכות מארח
                        </div>
                        <p style={styles.hostingPlayerText}>
                            כמארח פעיל, אתה יכול להירשם למשחק גם לפני פתיחת ההרשמה הרשמית!
                        </p>
                        <p style={styles.hostingPlayerExpiry}>
                            הזכות תקפה עד: {new Date(userProfile.hostingPlayerTagExpiry.seconds * 1000).toLocaleDateString('he-IL', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </p>
                    </div>
                )}

                {error && <div style={styles.errorBanner}>{error}</div>}

                {/* Players Section */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        שחקנים ({players.length}/{MAX_PLAYERS})
                    </h2>

                    {players.length === 0 ? (
                        <p style={styles.emptyText}>אין שחקנים עדיין. היה הראשון להצטרף!</p>
                    ) : (
                        <ul style={styles.list}>
                            {players.map((player, index) => (
                                <li key={player.id} style={styles.listItem}>
                                    <span style={styles.playerNumber}>{index + 1}.</span>
                                    <span style={styles.playerName}>{player.displayName}</span>
                                    {player.id === currentUser?.uid && (
                                        <span style={styles.badge}>אתה</span>
                                    )}
                                    {player.id === game.hostUserId && (
                                        <span style={styles.hostBadgeSmall}>מארח</span>
                                    )}
                                    {player.hostingPlayerTag &&
                                     player.hostingPlayerTagExpiry &&
                                     new Date(player.hostingPlayerTagExpiry.seconds * 1000) > new Date() && (
                                        <span style={styles.hostingPlayerBadge}>🏠 מארח פעיל</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Waitlist Section */}
                {(waitlist.length > 0 || players.length >= MAX_PLAYERS) && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            ⏳ רשימת המתנה ({waitlist.length})
                        </h2>

                        {waitlist.length === 0 ? (
                            <p style={styles.emptyText}>אין ממתינים עדיין</p>
                        ) : (
                            <ul style={styles.list}>
                                {waitlist.map((person, index) => (
                                    <li key={person.id} style={styles.listItem}>
                                        <span style={styles.playerNumber}>{index + 1}.</span>
                                        <span style={styles.playerName}>{person.displayName}</span>
                                        {person.id === currentUser?.uid && (
                                            <span style={styles.badgeWaitlist}>אתה</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Email notification toggle for current user if in waitlist */}
                        {isInWaitlist && (
                            <div style={styles.notificationSection}>
                                <div style={styles.notificationContent}>
                                    <div style={styles.notificationText}>
                                        <strong style={styles.notificationTitle}>📧 התראת אימייל</strong>
                                        <p style={styles.notificationDescription}>
                                            קבל התראה במייל כשתעבור לרשימת השחקנים
                                        </p>
                                    </div>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={emailNotification}
                                            onChange={handleEmailNotificationToggle}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={styles.actions}>
                    {isRegistered ? (
                        <div>
                            <p style={styles.statusText}>
                                {isInPlayers && '✅ אתה רשום כשחקן'}
                                {isInWaitlist && '⏳ אתה ברשימת ההמתנה'}
                            </p>
                            <button onClick={handleLeave} style={styles.buttonDanger}>
                                צא מהמשחק
                            </button>
                        </div>
                    ) : !isRegistrationOpen ? (
                        <div>
                            <p style={styles.statusText}>
                                🔒 ההרשמה עדיין לא נפתחה
                            </p>
                            <button disabled style={styles.buttonDisabled}>
                                ההרשמה תיפתח בקרוב
                            </button>
                        </div>
                    ) : (
                        <button onClick={handleJoin} style={styles.buttonPrimary}>
                            {players.length < MAX_PLAYERS ? 'הצטרף למשחק' : 'הצטרף לרשימת המתנה'}
                            {isHostingPlayer && registrationOpenTime && now < registrationOpenTime && ' (זכות מארח)'}
                        </button>
                    )}
                </div>

                {/* Admin Panel */}
                {userProfile?.isAdmin && (
                    <div style={styles.adminSection}>
                        <div style={styles.adminHeader}>
                            <h2 style={styles.adminTitle}>👨‍💼 ניהול מנהל</h2>
                            <button
                                onClick={() => setShowAdminPanel(!showAdminPanel)}
                                style={styles.adminToggleButton}
                            >
                                {showAdminPanel ? 'הסתר' : 'הצג'}
                            </button>
                        </div>

                        {showAdminPanel && (
                            <div style={styles.adminContent}>
                                <h3 style={styles.adminSubtitle}>הוסף משתמש לרשימת המתנה</h3>
                                <p style={styles.adminHelpText}>
                                    בחר משתמש להוספה לרשימת ההמתנה של המשחק
                                </p>

                                <div style={styles.userSelectList}>
                                    {allUsers
                                        .filter(user =>
                                            !players.some(p => p.id === user.id) &&
                                            !waitlist.some(w => w.id === user.id)
                                        )
                                        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'he'))
                                        .map(user => (
                                            <div key={user.id} style={styles.userSelectItem}>
                                                <div style={styles.userSelectInfo}>
                                                    <strong>{user.displayName}</strong>
                                                    <br />
                                                    <small style={styles.userSelectEmail}>{user.email}</small>
                                                </div>
                                                <button
                                                    onClick={() => handleAdminAddToWaitlist(user.id)}
                                                    style={styles.addToWaitlistButton}
                                                >
                                                    הוסף לרשימת המתנה
                                                </button>
                                            </div>
                                        ))}

                                    {allUsers.filter(user =>
                                        !players.some(p => p.id === user.id) &&
                                        !waitlist.some(w => w.id === user.id)
                                    ).length === 0 && (
                                        <p style={styles.emptyText}>כל המשתמשים כבר רשומים למשחק או לרשימת ההמתנה</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div style={styles.footer}>
                    <button onClick={() => navigate('/dashboard')} style={styles.linkButton}>
                        ← חזרה לדשבורד
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        padding: '30px 20px',
        direction: 'rtl',
    },
    card: {
        backgroundColor: '#1a1a2e',
        padding: '35px 30px',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        maxWidth: '700px',
        margin: '0 auto',
        border: '2px solid rgba(218, 165, 32, 0.3)',
    },
    topNav: {
        marginBottom: '20px',
        paddingBottom: '15px',
        borderBottom: '1px solid rgba(218, 165, 32, 0.2)',
        display: 'flex',
        justifyContent: 'flex-end',
    },
    backButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    },
    title: {
        textAlign: 'center',
        color: '#ffd700',
        marginBottom: '10px',
        fontSize: 'clamp(22px, 5vw, 32px)',
        fontWeight: 'bold',
        textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)',
    },
    hostBadge: {
        display: 'block',
        fontSize: '16px',
        color: '#22c55e',
        fontWeight: 'normal',
        marginTop: '10px',
    },
    hostBadgeSmall: {
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 'bold',
        boxShadow: '0 2px 6px rgba(22, 163, 74, 0.3)',
    },
    dateSection: {
        textAlign: 'center',
        marginBottom: '25px',
        marginTop: '20px',
    },
    date: {
        color: '#b0b0b0',
        fontSize: '16px',
        marginBottom: '12px',
        fontWeight: '500',
    },
    calendarButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    },
    infoBox: {
        background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.1) 100%)',
        padding: '15px',
        borderRadius: '12px',
        marginBottom: '20px',
        borderRight: '4px solid #2563eb',
        color: '#e0e0e0',
    },
    infoText: {
        margin: '8px 0 0 0',
        color: '#b0b0b0',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.6',
    },
    loading: {
        textAlign: 'center',
        padding: '40px',
        fontSize: '18px',
        direction: 'rtl',
        color: '#ffd700',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    error: {
        backgroundColor: 'rgba(220, 53, 69, 0.15)',
        color: '#ff6b6b',
        padding: '20px',
        borderRadius: '15px',
        textAlign: 'center',
        border: '1px solid rgba(220, 53, 69, 0.3)',
        maxWidth: '600px',
        margin: '40px auto',
    },
    errorBanner: {
        backgroundColor: 'rgba(220, 53, 69, 0.15)',
        color: '#ff6b6b',
        padding: '12px',
        borderRadius: '10px',
        marginBottom: '20px',
        textAlign: 'center',
        border: '1px solid rgba(220, 53, 69, 0.3)',
    },
    registrationClosedBanner: {
        backgroundColor: 'rgba(245, 158, 11, 0.15)',
        color: '#f59e0b',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        textAlign: 'center',
        border: '2px solid rgba(245, 158, 11, 0.3)',
    },
    countdownTitle: {
        fontWeight: 'bold',
        fontSize: '18px',
        marginBottom: '15px',
    },
    countdownTimer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '15px',
        flexWrap: 'wrap',
    },
    countdownSegment: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '60px',
    },
    countdownNumber: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#ffd700',
        textShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
        lineHeight: '1',
    },
    countdownLabel: {
        fontSize: '12px',
        marginTop: '5px',
        color: '#f59e0b',
        fontWeight: '600',
    },
    countdownSeparator: {
        fontSize: '28px',
        fontWeight: 'bold',
        color: '#ffd700',
        lineHeight: '1',
        paddingBottom: '15px',
    },
    countdownDate: {
        fontSize: '14px',
        color: '#f59e0b',
        marginTop: '10px',
        opacity: 0.9,
    },
    hostingPlayerBanner: {
        backgroundColor: 'rgba(34, 197, 94, 0.15)',
        color: '#22c55e',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        textAlign: 'center',
        border: '2px solid rgba(34, 197, 94, 0.3)',
    },
    hostingPlayerTitle: {
        fontWeight: 'bold',
        fontSize: '18px',
        marginBottom: '10px',
        color: '#ffd700',
        textShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
    },
    hostingPlayerText: {
        fontSize: '14px',
        marginBottom: '10px',
        color: '#22c55e',
    },
    hostingPlayerExpiry: {
        fontSize: '12px',
        color: '#86efac',
        fontStyle: 'italic',
    },
    section: {
        marginBottom: '30px',
    },
    sectionTitle: {
        fontSize: '22px',
        color: '#ffd700',
        marginBottom: '15px',
        paddingBottom: '10px',
        borderBottom: '2px solid rgba(218, 165, 32, 0.3)',
        textShadow: '0 2px 10px rgba(255, 215, 0, 0.2)',
    },
    list: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    listItem: {
        padding: '14px',
        borderBottom: '1px solid rgba(218, 165, 32, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        transition: 'background-color 0.2s ease',
    },
    playerNumber: {
        fontWeight: 'bold',
        color: '#ffd700',
        minWidth: '30px',
        fontSize: '16px',
    },
    playerName: {
        flex: 1,
        color: '#e0e0e0',
        fontSize: '15px',
    },
    badge: {
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
    },
    badgeWaitlist: {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)',
    },
    hostingPlayerBadge: {
        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 'bold',
        boxShadow: '0 2px 6px rgba(34, 197, 94, 0.3)',
    },
    emptyText: {
        color: '#999',
        fontStyle: 'italic',
        padding: '20px',
        textAlign: 'center',
    },
    actions: {
        marginTop: '30px',
        textAlign: 'center',
    },
    statusText: {
        marginBottom: '15px',
        color: '#22c55e',
        fontWeight: 'bold',
        fontSize: '16px',
    },
    buttonPrimary: {
        padding: '14px 35px',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '17px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    buttonDanger: {
        padding: '14px 35px',
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '17px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    buttonDisabled: {
        padding: '14px 35px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: '#9ca3af',
        border: 'none',
        borderRadius: '12px',
        fontSize: '17px',
        fontWeight: 'bold',
        cursor: 'not-allowed',
        boxShadow: '0 4px 15px rgba(107, 114, 128, 0.2)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        opacity: 0.6,
    },
    footer: {
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid rgba(218, 165, 32, 0.2)',
        textAlign: 'center',
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#ffd700',
        cursor: 'pointer',
        fontSize: '15px',
        textDecoration: 'none',
        fontWeight: 'bold',
        transition: 'color 0.3s ease',
    },
    adminSection: {
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '2px solid rgba(218, 165, 32, 0.3)',
    },
    adminHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
    },
    adminTitle: {
        fontSize: '22px',
        color: '#ffd700',
        margin: 0,
        textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)',
    },
    adminToggleButton: {
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    },
    adminContent: {
        background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(29, 78, 216, 0.05) 100%)',
        padding: '20px',
        borderRadius: '12px',
        border: '2px solid rgba(37, 99, 235, 0.3)',
    },
    adminSubtitle: {
        fontSize: '18px',
        color: '#e0e0e0',
        marginTop: 0,
        marginBottom: '10px',
    },
    adminHelpText: {
        color: '#b0b0b0',
        fontSize: '14px',
        marginBottom: '20px',
    },
    userSelectList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxHeight: '400px',
        overflowY: 'auto',
    },
    userSelectItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 15px',
        background: 'rgba(26, 26, 46, 0.6)',
        borderRadius: '10px',
        border: '1px solid rgba(218, 165, 32, 0.2)',
        transition: 'all 0.2s ease',
    },
    userSelectInfo: {
        flex: 1,
        color: '#e0e0e0',
    },
    userSelectEmail: {
        color: '#999',
        fontSize: '12px',
    },
    addToWaitlistButton: {
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
        whiteSpace: 'nowrap',
    },
    notificationSection: {
        marginTop: '20px',
        padding: '20px',
        background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.1) 100%)',
        borderRadius: '12px',
        border: '2px solid rgba(37, 99, 235, 0.3)',
    },
    notificationContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
    },
    notificationText: {
        flex: 1,
    },
    notificationTitle: {
        color: '#ffd700',
        fontSize: '16px',
        display: 'block',
        marginBottom: '5px',
    },
    notificationDescription: {
        color: '#b0b0b0',
        fontSize: '14px',
        margin: '5px 0 0 0',
        lineHeight: '1.4',
    },
};
